import { app } from './core/App';
import { RelicNameValues } from './level/relics/relic';
import { WorldName, WorldNameValues } from './level/worlds/world';

export interface GameData {
  [GameDataHandler.keys.HighScore]?: number;
  [GameDataHandler.keys.UnlockedWorlds]: WorldNameValues[];
  [GameDataHandler.keys.CollectedRelics]: RelicNameValues[];
  [GameDataHandler.keys.NewRelics]: RelicNameValues[];
  [GameDataHandler.keys.NewWorlds]: RelicNameValues[];
}

export class GameDataHandler {
  public static keys = {
    Score: 'score',
    HighScore: 'highScore',
    UnlockedWorlds: 'unlockedWorlds',
    CollectedRelics: 'collectedRelics',
    NewRelics: 'newRelics',
    NewWorlds: 'newWorlds'
  } as const;
  public static events = {
    ChangeScore: 'changedata-score',
    ChangeHighScore: 'changedata-highScore',
    ChangeUnlockedWorlds: 'changedata-unlockedWorlds',
    ChangeCollectedRelics: 'changedata-collectedRelics',
    ChangeNewRelics: `changedata-${[GameDataHandler.keys.NewRelics]}`,
    ChangeNewWorlds: `changedata-${[GameDataHandler.keys.NewWorlds]}`
  } as const;

  private gameData: GameData;

  constructor(gameData: GameData) {
    const defaultData: GameData = {
      [GameDataHandler.keys.HighScore]: 0,
      [GameDataHandler.keys.UnlockedWorlds]: [WorldName.Shipwreck],
      [GameDataHandler.keys.CollectedRelics]: [],
      [GameDataHandler.keys.NewRelics]: [],
      [GameDataHandler.keys.NewWorlds]: []
    };

    this.gameData = Object.assign({}, defaultData, gameData);
  }

  public static collectRelic(scene: Phaser.Scene, relicName: RelicNameValues) {
    let unlockedRelics = scene.game.registry.get(GameDataHandler.keys.CollectedRelics) as RelicNameValues[];
    let newRelics = scene.game.registry.get(GameDataHandler.keys.NewRelics) as RelicNameValues[];
    if (unlockedRelics.indexOf(relicName) == -1) {
      unlockedRelics.push(relicName);
      newRelics.push(relicName);
      scene.game.registry.set(GameDataHandler.keys.NewRelics, newRelics);
      scene.game.registry.set(GameDataHandler.keys.CollectedRelics, unlockedRelics);
    }
  }

  public static unlockWorld(scene: Phaser.Scene, worldName: WorldNameValues) {
    let unlockedWorlds = scene.game.registry.get(GameDataHandler.keys.UnlockedWorlds) as WorldNameValues[];
    let newWorlds = scene.game.registry.get(GameDataHandler.keys.NewWorlds) as WorldNameValues[];
    if (unlockedWorlds.indexOf(worldName) == -1) {
      unlockedWorlds.push(worldName);
      newWorlds.push(worldName);
      scene.game.registry.set(GameDataHandler.keys.NewWorlds, newWorlds);
      scene.game.registry.set(GameDataHandler.keys.UnlockedWorlds, unlockedWorlds);
    }
  }

  public static clearNewWorlds(scene: Phaser.Scene) {
    scene.game.registry.set(GameDataHandler.keys.NewWorlds, []);
  }

  public static clearNewRelics(scene: Phaser.Scene) {
    scene.game.registry.set(GameDataHandler.keys.NewRelics, []);
  }

  // Apply default values that we don't want saved in gameData.
  public applyDefaultsToPhaserRegistry(game: Phaser.Game) {
    game.registry.set({
      score: 0
    });
  }

  // Apply any loaded values or sane defaults for non-loaded savable values.
  public applySavedDataToPhaserRegistry(game: Phaser.Game) {
    game.registry.set(this.gameData);
  }

  // Bind all the events for data changes.
  public bindToPhaserRegistry(game: Phaser.Game) {
    game.registry.events.on(GameDataHandler.events.ChangeHighScore, this.onChangeHighScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeCollectedRelics, this.onRelicCollected, this);
    game.registry.events.on(GameDataHandler.events.ChangeNewRelics, this.onNewRelicCollected, this);
    game.registry.events.on(GameDataHandler.events.ChangeUnlockedWorlds, this.onWorldUnlocked, this);
    game.registry.events.on(GameDataHandler.events.ChangeNewWorlds, this.onNewWorldUnlocked, this);
  }

  protected onChangeHighScore(parent: any, value: any, previousValue: any) {
    this.gameData.highScore = value;
    app.dataHandler.save(this.gameData);
  }

  protected onRelicCollected(parent: any, value: any, previousValue: any) {
    this.gameData.collectedRelics = value;
    app.dataHandler.save(this.gameData);
  }

  protected onNewRelicCollected(parent: any, value: any, previousValue: any) {
    this.gameData.newRelics = value;
    app.dataHandler.save(this.gameData);
  }

  protected onWorldUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData.unlockedWorlds = value;
    app.dataHandler.save(this.gameData);
  }

  protected onNewWorldUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData.newWorlds = value;
    app.dataHandler.save(this.gameData);
  }
}
